import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  Text,
  useDisclosure,
  RegionError
} from '~/design_system'
import { useAccountMeQuery } from '~/api/accounts'
import { formatDateTime } from '~/common/tzUtils'
import { useNotificationsStore } from '~/components/notifications/state'
import { SadStates } from '~/components/SadStates'
import { CardBody, CardHeading } from '~/components/v2/Card'
import { CountPager } from '~/components/v2/CountPager'
import { ItemsList, ItemsListFooter, ItemsListItem, ItemsListItems, ItemsListLink } from '~/components/v2/ItemsList'
import { Search } from '~/components/v2/Search'
import { HeaderIconButton, MutedSkeleton, UNREAD_BG_COLOR, WidgetEmptyState } from '~/home/common'
import useUuid from '~/hooks/useUuid'
import { Close, Coffee, Rsvp, Search as SearchIcon } from '~/icons'
import { Msg, useMsg } from '~/common/localizationUtils'
import { $thm } from '~/common/themeSx'

/**
 * @param props {Record}
 * @param props.setTabIndicator {(val: boolean | string) => void}
 * @returns {JSX.Element}
 */
export const Notifications = ({ setTabIndicator = () => {} }) => {
  const { error, loading, actions, page, notifications, onlyUnread, totalPages, totalResults, queryString } =
    useNotificationsStore()
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure()
  const itemsListContainerRef = useRef()
  const msg = useMsg()

  const [paging, setPaging] = useState(false)
  useEffect(() => {
    actions.init()
  }, [])

  useEffect(() => {
    setTabIndicator(totalResults > 0)
  }, [totalResults])

  const turnPage = async next => {
    const fn = next ? actions.nextPage : actions.prevPage
    if (!paging) {
      setPaging(true)
      await fn()
      setPaging(false)
      // Scroll back to top of list
      if (itemsListContainerRef.current) {
        itemsListContainerRef.current.scrollTop = 0
      }
    }
  }

  const onSearch = useCallback(
    /*
        eslint-disable-next-line react-hooks/preserve-manual-memoization --

        ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
        Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
        There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
        couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

        Issue: `actions` is used inside the callback but deps are empty.
        Possible fix approach:
          // Option 1: Add `actions` to the dependency array:
          const onSearch = useCallback(searchTerm => actions.setQueryString(searchTerm), [actions])

          // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable)
    */
    searchTerm => actions.setQueryString(searchTerm),
    []
  )
  const noSearchResults = !loading && !!queryString && totalResults === 0
  const noRegularResults = !loading && !queryString && totalResults === 0

  return (
    <Box role="group" aria-label="Notifications">
      <Stack py={4} borderBottom="1px" borderColor="gray.300" sx={$thm({ px: { $base: 6, $canvas: 0 } })}>
        <Flex alignItems="center" flexWrap="wrap">
          <CardHeading pr={6} flexGrow={0}>
            {msg('notification.card.heading')}
            {!loading && (
              <Badge
                data-testid="badge-txt-notification-results"
                display="inline"
                color="gray.500"
                ml={4}
                fontSize="md">
                {totalResults}
              </Badge>
            )}
          </CardHeading>
          <Flex flexGrow={1} justifyContent="flex-end" alignItems="center">
            <FormControl display="flex" justifyContent="flex-end" alignItems="center" mr={3}>
              <FormLabel htmlFor="only-unread-notifications" fontSize="sm" fontWeight="normal" mb={0}>
                {msg('notification.card.unread_only')}
              </FormLabel>
              <Switch
                id="only-unread-notifications"
                isChecked={onlyUnread}
                onChange={e => actions.setOnlyUnread(e.target.checked)}
              />
            </FormControl>
            <HeaderIconButton
              aria-label={msg('notification.card.mark_all_read')}
              icon={<Rsvp />}
              onClick={() => actions.markAllRead().then(() => onSearchClose())}
              isDisabled={!onlyUnread || noSearchResults || noRegularResults}
              mr={1}
            />
            <HeaderIconButton
              data-testid="notifications-search-btn"
              aria-label={isSearchOpen ? msg('notification.card.close_search') : msg('notification.card.open_search')}
              icon={isSearchOpen ? <Close /> : <SearchIcon />}
              mr={-2}
              onClick={() => {
                if (isSearchOpen) {
                  onSearchClose()
                  onSearch(null)
                } else {
                  onSearchOpen()
                }
              }}
            />
          </Flex>
        </Flex>
        {isSearchOpen && <Search data-testid="notifications-search-txt" onChange={onSearch} autoFocus />}
      </Stack>
      <Stack spacing={4}>
        <Box>
          <SadStates
            states={[
              {
                when: error,
                render: () => <RegionError onRefresh={actions.init} />
              },
              {
                when: () => loading,
                render: () => <SkeletonGroup />
              },
              {
                when: () => noSearchResults,
                render: () => (
                  <WidgetEmptyState icon={SearchIcon} m={0}>
                    <Text>
                      <Msg id="common.ui.no_results_found" />
                    </Text>
                  </WidgetEmptyState>
                )
              },
              {
                when: () => noRegularResults,
                render: () => (
                  <WidgetEmptyState icon={Coffee} m={0}>
                    <Text>
                      <Msg id="common.ui.empty_state_caught_up" />
                    </Text>
                  </WidgetEmptyState>
                )
              }
            ]}>
            {() => (
              <CardBody p={0}>
                <ItemsList border={0}>
                  <ItemsListItems ref={itemsListContainerRef}>
                    {notifications.map(n => (
                      <Notification key={n.id} notification={n} />
                    ))}
                  </ItemsListItems>
                  {totalPages > 1 && (
                    <ItemsListFooter py={3} justify="center">
                      <CountPager
                        pageNumberTestId="NotificationPageNumbers"
                        onNextPage={() => turnPage(true)}
                        onPrevPage={() => turnPage(false)}
                        page={page}
                        totalPages={totalPages}
                      />
                    </ItemsListFooter>
                  )}
                </ItemsList>
              </CardBody>
            )}
          </SadStates>
        </Box>
      </Stack>
    </Box>
  )
}

export function Notification({ notification: n }) {
  const id = `itemlistlink_${useUuid()}`
  const { data: { timezone, locale } = {} } = useAccountMeQuery()
  return (
    <ItemsListItem
      data-testid={`notification-id-${n.id}`}
      aria-labelledby={id}
      data-unread={!n.is_viewed}
      backgroundColor={n.is_viewed ? 'transparent' : UNREAD_BG_COLOR}>
      <ItemsListLink href={n.id != null ? `/notifications/view/${n.id}` : '#'} py={4}>
        <Stack>
          <Text id={id} lineHeight="short" wordBreak="break-word">
            {n.message}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {formatDateTime(n.created_at, timezone, locale)}
          </Text>
        </Stack>
      </ItemsListLink>
    </ItemsListItem>
  )
}

function SkeletonGroup() {
  const item = (
    <ItemsListItem>
      <Stack w="full">
        <MutedSkeleton h="22px" w="full" />
        <MutedSkeleton h="22px" w="50%" />
      </Stack>
    </ItemsListItem>
  )
  return (
    <CardBody p={0}>
      <ItemsList border={0}>
        <ItemsListItems>
          {item}
          {item}
          {item}
          {item}
        </ItemsListItems>
      </ItemsList>
    </CardBody>
  )
}
